// Shared rarity scheme for cosmetics (sleeves, duel disks, uniforms, colourways).
// Distinct from rarity.ts (which is Yu-Gi-Oh's CARD rarities N/R/SR/UR). Same vocabulary
// here as the gacha-cosmetic convention: common / rare / epic / legendary.
//
// Used by the shop AND the customizer so a legendary gold colourway in your inventory
// reads the same as a legendary tag in the shop, no drift between surfaces.

export type CosmeticRarity = "common" | "rare" | "epic" | "legendary";

/** Tier text colour for small uppercase rarity labels under items. */
export const RARITY_TEXT: Record<CosmeticRarity, string> = {
  common: "text-ink-dim",
  rare: "text-cyan",
  epic: "text-purple",
  legendary: "text-orange",
};

// Note on chamfered tiles: chamfer-btn uses a clip-path which clips outer box-shadows (so
// `ring-*` is invisible on tiles). Use BORDERS instead, they respect the clip path and render
// at the chamfered edge, the same way the existing selected-state cyan border does.

/** Selected-state border (paired with cyan glow when a tier item is currently equipped). */
export const RARITY_BORDER: Record<CosmeticRarity, string> = {
  common: "border-ink-dim/40",
  rare: "border-cyan",
  epic: "border-purple",
  legendary: "border-orange",
};

/** Resting border colour for owned items, strong enough to read tier at a glance. Common gets
 *  no override (it stays at the inherited dim cyan, our baseline for any UI element). */
export const RARITY_BORDER_REST: Record<CosmeticRarity, string> = {
  common: "",
  rare: "border-cyan/70",
  epic: "border-purple",
  legendary: "border-orange",
};

/** Background tint per tier, pairs with the borders so a catalog tile reads its rarity by COLOUR,
 *  not just an edge. Mirrors the box-shop GACHA_CARD tints (rare cyan / epic purple / legendary gold). */
export const RARITY_BG: Record<CosmeticRarity, string> = {
  common: "bg-void-2/50",
  rare: "bg-cyan/10",
  epic: "bg-purple/15",
  legendary: "bg-orange/15",
};

/** Back-compat aliases, older callsites that used "ring" terminology. */
export const RARITY_RING = RARITY_BORDER;
export const RARITY_RING_REST = RARITY_BORDER_REST;

// ─── Gacha rarity (box pulls) ──────────────────────────────────────────────────
// The box economy uses the MD-style four tiers common/rare/super/ultra (see GachaRarity in
// world-protocol). Styling parallels the cosmetic tiers above: rare=cyan, super=purple, ultra=gold.
export type GachaRarity = "common" | "rare" | "super" | "ultra";

export const GACHA_LABEL: Record<GachaRarity, string> = {
  common: "Common",
  rare: "Rare",
  super: "Super Rare",
  ultra: "Ultra Rare",
};
export const GACHA_TEXT: Record<GachaRarity, string> = {
  common: "text-ink-dim",
  rare: "text-cyan",
  super: "text-purple",
  ultra: "text-orange",
};
export const GACHA_BORDER: Record<GachaRarity, string> = {
  common: "border-ink-dim/40",
  rare: "border-cyan",
  super: "border-purple",
  ultra: "border-orange",
};
/** Reveal-time glow accent (the brighter, the rarer). Common gets none. */
export const GACHA_GLOW: Record<GachaRarity, string> = {
  common: "",
  rare: "glow-cyan",
  super: "shadow-[0_0_28px_rgba(199,155,255,0.7)]",
  ultra: "shadow-[0_0_36px_rgba(255,179,64,0.85)]",
};

/** Filled rarity badge (bg fill + text), bolder than a plain text label, so the tier reads at a
 *  glance on a shop tile. */
export const GACHA_BADGE: Record<GachaRarity, string> = {
  common: "bg-ink-dim/20 text-ink-dim",
  rare: "bg-cyan/25 text-cyan",
  super: "bg-purple/30 text-purple",
  ultra: "bg-gradient-to-r from-orange/35 to-gold/30 text-orange",
};
/** Whole-tile styling per rarity (border + background tint) so rarer items POP via colour, not just
 *  a (chamfer-clipped) ring, common stays neutral, ultra glows gold. */
export const GACHA_CARD: Record<GachaRarity, string> = {
  common: "border-cyan-dim/20 bg-void-2/40",
  rare: "border-cyan/50 bg-cyan/10",
  super: "border-purple/60 bg-purple/15",
  ultra: "border-orange/70 bg-orange/15",
};

// ── Beta Exclusive ─────────────────────────────────────────────────────────────
// A display-only tier ABOVE ultra for the grant-only Beta Gold Duel Disk: a bright solid-gold badge
// (dark text) + a gold-glowing card, so it reads as one-of-a-kind, not just "ultra".
export const BETA_LABEL = "Beta Exclusive";
export const BETA_BADGE = "bg-gradient-to-r from-gold via-orange/90 to-gold text-void";
export const BETA_CARD = "border-gold bg-gold/15 shadow-[0_0_22px_rgba(255,179,64,0.3)]";
